// Import the functions you need from the SDKs you need
import { getApp, getApps, initializeApp } from "firebase/app";
import {
  browserLocalPersistence,
  browserSessionPersistence,
  getAuth,
  indexedDBLocalPersistence,
  initializeAuth,
} from "firebase/auth"; // For authentication
import { readSignedInBefore } from "../utils/lastRole";
import { initializeFirestore, disableNetwork } from "firebase/firestore"; // For Firestore database
import { getStorage } from "firebase/storage"; // For Storage
import { getMessaging, isSupported as isMessagingSupported } from "firebase/messaging";
import { getFunctions } from "firebase/functions";

// Your web live app's Firebase configuration

// Two Firebase projects:
//   • team-edelstein   — LIVE (production data)
//   • team-henry-demo  — DEV / SANDBOX (safe for testing)
// Local dev (`npm start` → NODE_ENV "development") uses the SANDBOX so testing
// never touches live data. A production build (`npm run build` → "production")
// uses the LIVE project. A dev-only console line below confirms which is active.

const liveConfig = {
  apiKey: "AIzaSyDo_FqutSqnAdeLAgff4ggRcWX0pg2h5XM",
  authDomain: "team-edelstein.firebaseapp.com",
  projectId: "team-edelstein",
  storageBucket: "team-edelstein.firebasestorage.app",
  messagingSenderId: "627842934381",
  appId: "1:627842934381:web:1262ee1777d45c7cb6c164",
};

const demoConfig = {
  apiKey: "AIzaSyBGb4fcrb7VFRYXDiPKAETSa6ueGcw4qH8",
  authDomain: "team-henry-demo.firebaseapp.com",
  projectId: "team-henry-demo",
  storageBucket: "team-henry-demo.firebasestorage.app",
  messagingSenderId: "475392448682",
  appId: "1:475392448682:web:6b2821dc598ac12d77d8d3",
  measurementId: "G-WZQB28ZFLC",
};

// Jest sets NODE_ENV to "test". That is NOT "development", so before this guard
// existed the whole test suite booted against the LIVE project and opened real
// network connections to production data. Requests were rejected as
// unauthenticated, but only the security rules stood between a test run and real
// records, and leaked Firestore clients kept timers alive that made unrelated
// suites fail at random. Tests now get an isolated project that does not exist.
// The SDK still initializes for real, because src/utils/firebasefunctions.js
// calls getAuth() at import time and needs a default app, but nothing it does
// can reach team-edelstein.
const testConfig = {
  apiKey: "test-api-key-not-a-real-credential",
  authDomain: "localhost",
  projectId: "team-edelstein-tests-do-not-create",
  storageBucket: "team-edelstein-tests-do-not-create.appspot.com",
  messagingSenderId: "000000000000",
  appId: "1:000000000000:web:test",
};

const isTest = process.env.NODE_ENV === "test";

// Escape hatch: REACT_APP_USE_LIVE=1 makes `npm start` target LIVE — needed
// when a new Settings screen must write live config before the next cPanel
// upload (e.g. pasting RingCentral credentials). Use via a temporary
// .env.development.local file and DELETE it when done.
const useLive =
  !isTest &&
  (process.env.NODE_ENV !== "development" ||
    process.env.REACT_APP_USE_LIVE === "1");

const firebaseConfig = isTest ? testConfig : useLive ? liveConfig : demoConfig;

if (process.env.NODE_ENV === "development") {
  // eslint-disable-next-line no-console
  console.info(
    useLive
      ? "%c[Firebase] ⚠️ DEV POINTED AT LIVE → project:"
      : "%c[Firebase] DEV sandbox → project:",
    useLive
      ? "color:#dc2626;font-weight:bold"
      : "color:#16a34a;font-weight:bold",
    firebaseConfig.projectId,
  );
}

// Initialize Firebase
const app = getApps().length > 0 ? getApp() : initializeApp(firebaseConfig);

// Firebase Analytics was removed on 2026-09-14. The live project has no
// measurement ID, so it collected nothing, yet every launch fetched a Firebase
// config and then Google's 88 KB gtag script. Nothing in the app read it. If
// Analytics is ever wanted, link Google Analytics in the Firebase console, add
// the measurementId, and start it after the first screen has painted.

// Messaging is an optional, browser-dependent service. On some mobile contexts
// (iOS Safari, private mode, blocked storage/IndexedDB) its initialization can
// throw, and because this module is imported before React mounts, an unguarded
// throw here blanks the whole app. Init defensively so a missing capability
// degrades to "no push", never a dead app.
//
// Messaging (FCM/push) is browser-dependent: getMessaging() throws — as an
// UNCAUGHT async rejection — on browsers without push support, notably iOS
// Safari outside an installed PWA, and under jsdom in tests. A plain try/catch
// can't catch that async throw. Gate on isSupported() so getMessaging() is only
// ever called where it works; elsewhere `messaging` stays null and push
// features no-op. `messagingReady` resolves to the instance (or null) for
// consumers that need to wait for initialization.
let messaging = null;
// Under jest this promise would stay pending past the end of a suite and show up
// as a worker that "failed to exit gracefully", so skip it entirely in tests.
const messagingReady = isTest
  ? Promise.resolve(null)
  : isMessagingSupported()
  .then((supported) => {
    if (!supported) return null;
    try {
      messaging = getMessaging(app);
    } catch (err) {
      console.warn("[Firebase] Messaging unavailable:", err && err.message);
    }
    return messaging;
  })
  .catch(() => null);

// Initialize Firebase Services
//
// Sign-in (2026-09-14, speed wave 5). getAuth(app) brings Google's popup sign-in
// helper, and on an iPhone, iPad or Safari Firebase downloads that helper (from two
// extra sites) BEFORE it reports who is signed in, so every launch waited on it.
// Only the login page's Continue With Google button needs it.
//
// On a device where someone has signed in before (utils/lastRole.js), start without
// the helper. The saved sign-in is found exactly as before: this is getAuth's own
// storage list, in getAuth's order. The Google button hands the helper over at the tap
// (login-form/loginForm.jsx). When nobody is signed in any more, App.js wipes the note
// and reloads once, so the login page starts with the helper again. With no note
// (a first launch, or after signing out), getAuth(app) keeps the helper ready.
const authStartedWithoutPopupHelper = readSignedInBefore();
const auth = authStartedWithoutPopupHelper
  ? initializeAuth(app, {
      persistence: [indexedDBLocalPersistence, browserLocalPersistence, browserSessionPersistence],
    })
  : getAuth(app);
// ignoreUndefinedProperties: an undefined field means "no value — don't write
// it", never "reject the whole save". Sparse husk deals (e.g. the sourceDealId
// patch deals with no propertyphoto/leadsource/propertyaddress) otherwise make
// every edit-wizard save explode with "Unsupported field value: undefined".
// To CLEAR a field, write null (see deletedAt in firebasefunctions.js).
const firestored = initializeFirestore(app, { ignoreUndefinedProperties: true });
// Under jest, take Firestore offline immediately. Without this, any component or
// helper that opens an onSnapshot listener starts a real gRPC stream to Google.
// Those streams outlive the suite that created them and their failures surface
// inside whichever unrelated test file shares the jest worker, which is what made
// the suite fail at random. Offline, reads serve from an empty local cache and no
// connection is ever attempted.
if (isTest) {
  disableNetwork(firestored).catch(() => {});
}
const storage = getStorage(app);
const functionsClient = getFunctions(app, "us-central1");

// Exported so the Fast Lane reader (utils/fastUserRead.js) can refuse to run under jest: it loads
// firebase/firestore/lite, which has no disableNetwork, so nothing could stop it opening a real connection.
export {
  isTest,
  app,
  auth,
  authStartedWithoutPopupHelper,
  firestored,
  storage,
  messaging,
  messagingReady,
  functionsClient,
  firebaseConfig,
};
